// src/components/Sidebare/ModernSidebar.jsx
import React, { useMemo, useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaChevronDown, FaChevronUp, FaBell, FaChartBar, FaHeart, FaPaw, FaSyringe,
  FaPills, FaWeight, FaSeedling, FaUtensils, FaBreadSlice, FaExclamationTriangle,
  FaChevronLeft, FaChevronRight, FaCreditCard, FaTag, FaUsers, FaUserShield, FaHeadset, FaEnvelope,
  FaGraduationCap
} from "react-icons/fa";
import { IoHome } from "react-icons/io5";
import { MdOutlineLanguage, MdOutlineLocalPharmacy } from "react-icons/md";
import { FaLocationDot } from "react-icons/fa6";
import { GiGoat } from "react-icons/gi";
import { CiLogout } from "react-icons/ci";
import { RiLuggageCartFill } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { RiDashboardHorizontalLine } from "react-icons/ri";
import { UserContext } from "../../Context/UserContext";
import { jwtDecode } from "jwt-decode";
import { isImpersonating } from "../../utils/authToken";
import { useSubscription } from "../../Context/SubscriptionContext";
import { FaArrowUp } from "react-icons/fa";
import SidebarSubscriptionCard from "../subscription/SidebarSubscriptionCard";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../utils/permissions";
import { SIDEBAR_MENU_PERMISSIONS } from "../../utils/routePermissions";

import "./modern-sidebar.css";

export default function ModernSidebar({
  isOpen,
  isRTL = false,
  notificationCount = 0,
  onLogout,
  onChangeLanguage,
  onToggle,
  hideRailWhenOpen = true,
}) {
  const { pathname } = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const { t } = useTranslation();

  const { Authorization } = useContext(UserContext);
  const userRole = Authorization ? jwtDecode(Authorization).role : null;
  // During "login as user", show the user sidebar even if cookie still has admin JWT
  const isAdmin = userRole === "admin" && !isImpersonating();
  
  // Get subscription status for upgrade badge
  const { subscriptionStatus, daysUntilExpiry } = useSubscription();
  const showUpgradeBadge = !isAdmin && (subscriptionStatus !== 'active' || (daysUntilExpiry !== null && daysUntilExpiry <= 5));
  
  // Check RBAC permissions
  const { checkPermission, permissions, isEmployee } = usePermissions();
  const canViewRoles = checkPermission(PERMISSIONS.ROLES_READ);
  const canViewEmployees = checkPermission(PERMISSIONS.EMPLOYEES_READ);

  // Helper function to check if user can access a path (used in both useMemo and render)
  const canAccessPath = (path) => {
    if (!path) return true;
    // Always allow Home, Support, and Notifications
    if (path === "/" || path === "/support" || path === "/notificationPage") return true;
    
    const requiredPermission = SIDEBAR_MENU_PERMISSIONS[path];
    // If no permission required (null), allow access
    if (requiredPermission === null) return true;
    // If permission is undefined and user is employee, deny access
    if (isEmployee() && requiredPermission === undefined) return false;
    // If permission is undefined and user is not employee, allow access (backward compatibility)
    if (!isEmployee() && requiredPermission === undefined) return true;
    // Check if user has the required permission
    return checkPermission(requiredPermission);
  };

  const baseMenu = useMemo(() => {
    let menu;
    if (isAdmin) {
      menu = [
        {
          title: t("MAIN MENU"),
          items: [
            { name: t("Home"), icon: <IoHome />, path: "/" },
            {
              name: t("support.supportTickets") || "Support Tickets",
              icon: <FaHeadset />,
              subItems: [
                { name: t("support.allTickets") || "All Tickets", path: "/admin/support" },
                { name: t("support.statistics") || "Statistics", path: "/admin/support/statistics" },
              ],
            },
          ],
        },
        {
          title: t("Admin"),
          items: [
            {
              name: t("Dashboard"),
              icon: <RiDashboardHorizontalLine />,
              subItems: [
                { name: t("Dashboard"), path: "/dashboard" },
                { name: t("Admin Dashboard"), path: "/AdminDashboard" },
              ],
            },
            {
              name: t("Vaccine Type"),
              icon: <FaSyringe />,
              subItems: [
                { name: t("Add Vaccine Type"), path: "/addVaccineType" },
                { name: t("Vaccine Type Table"), path: "/vaccineTypetable" },
              ],
            },
            {
              name: t("Plans"),
              icon: <FaCreditCard />,
              subItems: [
                { name: t("Add Plan"), path: "/addPlan" },
                { name: t("Plans Table"), path: "/planTable" },
              ],
            },
            {
              name: t("Discount Codes"),
              icon: <FaTag />,
              subItems: [
                { name: t("Add Discount Code"), path: "/addDiscountCode" },
                { name: t("Discount Codes Table"), path: "/discountCodeTable" },
              ],
            },
            {
              name: t("course.admin.section", "Course Management"),
              icon: <FaGraduationCap />,
              subItems: [
                { name: t("course.admin.dashboard", "Dashboard"), path: "/admin/course" },
                { name: t("course.admin.students", "Students"), path: "/admin/course/students" },
                { name: t("course.admin.registrations", "Registrations"), path: "/admin/course/registrations" },
                { name: t("course.admin.payments", "Payments"), path: "/admin/course/payments" },
                { name: t("course.admin.lectures", "Lectures"), path: "/admin/course/lectures" },
                { name: t("course.admin.paymentSettings", "Payment Settings"), path: "/admin/course/payment-settings" },
              ],
            },
          ],
        },
      ];
    } else {
      menu = [
      {
        title: t("MAIN MENU"),
        items: [
          { name: t("Home"), icon: <IoHome />, path: "/" },
          { name: t("Notifications"), icon: <FaBell />, path: "/notificationPage", badge: notificationCount },
          { name: t("Support") || "Support", icon: <FaHeadset />, path: "/support" },
          { name: t("support.myMessages") || "Messages", icon: <FaEnvelope />, path: "/dashboard/support" },
        ],
      },
      {
        title: t("User"),
        items: [
          ...(canAccessPath("/userDashboard") ? [{
            name: t("Dashboard"),
            icon: <RiDashboardHorizontalLine />,
            path: "/userDashboard",
          }] : []),
          //Hide "Upgrade now" for employees
          ...(!isEmployee() && canAccessPath("/subscription/plans") ? [{
            name: t("Upgrade now"),
            icon: <FaArrowUp />,
            path: "/subscription/plans",
            badge: showUpgradeBadge ? "!" : null,
          }] : []),
        ].filter(Boolean),
      },
      {
        title: t("Supplier"),
        items: [
          {
            name: t("Supplier"),
            icon: <RiLuggageCartFill />,
            subItems: [
              ...(canAccessPath("/supplierTable") ? [{ name: t("Suppliers Data"), path: "/supplierTable" }] : []),
              ...(canAccessPath("/supplier") ? [{ name: t("Add Suppliers"), path: "/supplier" }] : []),
            ].filter(Boolean),
          },
        ].filter(item => item.subItems && item.subItems.length > 0),
      },
      {
        title: t("Basic information"),
        items: [
          {
            name: t("Location Shed"),
            icon: <FaLocationDot />,
            subItems: [
              ...(canAccessPath("/locationTable") ? [{ name: t("Location Data"), path: "/locationTable" }] : []),
              ...(canAccessPath("/locationPost") ? [{ name: t("Add Location"), path: "/locationPost" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("Breed"),
            icon: <GiGoat />,
            subItems: [
              ...(canAccessPath("/breedTable") ? [{ name: t("Breed Data"), path: "/breedTable" }] : []),
              ...(canAccessPath("/breedPost") ? [{ name: t("Add Breed"), path: "/breedPost" }] : []),
            ].filter(Boolean),
          },
        ].filter(item => item.subItems && item.subItems.length > 0),
      },
      {
        title: t("Animal Management"),
        items: [
          {
            name: t("animal"),
            icon: <FaPaw />,
            subItems: [
              ...(canAccessPath("/animals") ? [{ name: t("animals Data"), path: "/animals" }] : []),
              ...(canAccessPath("/AnimalsDetails") ? [{ name: t("add Animal"), path: "/AnimalsDetails" }] : []),
              ...(canAccessPath("/animalCost") ? [{ name: t("animal Cost"), path: "/animalCost" }] : []),
            ].filter(Boolean),
          },
        ].filter(item => item.subItems && item.subItems.length > 0),
      },
      {
        title: t("Health and Breeding"),
        items: [
          {
            name: t("mating"),
            icon: <FaHeart />,
            subItems: [
              ...(canAccessPath("/matingTable") ? [{ name: t("mating Data"), path: "/matingTable" }] : []),
              ...(canAccessPath("/mating") ? [{ name: t("add Mating"), path: "/mating" }] : []),
              ...(canAccessPath("/matingLocation") ? [{ name: t("add By LocationShed"), path: "/matingLocation" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("vaccine"),
            icon: <FaSyringe />,
            subItems: [
              ...(canAccessPath("/vaccineTable") ? [{ name: t("vaccine Data"), path: "/vaccineTable" }] : []),
              ...(canAccessPath("/Vaccinebyanimalsstable") ? [{ name: t("vaccine Animal Data"), path: "/Vaccinebyanimalsstable" }] : []),
              ...(canAccessPath("/addVaccine") ? [{ name: t("Add Vaccine"), path: "/addVaccine" }] : []),
              ...(canAccessPath("/vaccinebytagid") ? [{ name: t("Add By Animal"), path: "/vaccinebytagid" }] : []),
              ...(canAccessPath("/vaccinebylocationshed") ? [{ name: t("Add By Location"), path: "/vaccinebylocationshed" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("Pharmacy"),
            icon: <MdOutlineLocalPharmacy />,
            subItems: [
              ...(canAccessPath("/treatmentTable") ? [{ name: t("Pharmacy Data"), path: "/treatmentTable" }] : []),
              ...(canAccessPath("/treatment") ? [{ name: t("add Pharmacy"), path: "/treatment" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("treatment"),
            icon: <FaPills />,
            subItems: [
              ...(canAccessPath("/treatAnimalTable") ? [{ name: t("show By Animal"), path: "/treatAnimalTable" }] : []),
              ...(canAccessPath("/treatmentAnimal") ? [{ name: t("add By Animal"), path: "/treatmentAnimal" }] : []),
              ...(canAccessPath("/treatmentLocation") ? [{ name: t("add By Location"), path: "/treatmentLocation" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("weight"),
            icon: <FaWeight />,
            subItems: [
              ...(canAccessPath("/weightTable") ? [{ name: t("weight Data"), path: "/weightTable" }] : []),
              ...(canAccessPath("/weight") ? [{ name: t("add Weight"), path: "/weight" }] : []),
              ...(canAccessPath("/withGrowthData") ? [{ name: t("Animal Growth"), path: "/withGrowthData" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("breeding"),
            icon: <FaSeedling />,
            subItems: [
              ...(canAccessPath("/breadingTable") ? [{ name: t("breeding Data"), path: "/breadingTable" }] : []),
              ...(canAccessPath("/breeding") ? [{ name: t("add Breeding"), path: "/breeding" }] : []),
            ].filter(Boolean),
          },
        ].filter(item => item.subItems && item.subItems.length > 0),
      },
      {
        title: t("Feeding and Reports"),
        items: [
          {
            name: t("feeding"),
            icon: <FaUtensils />,
            subItems: [
              ...(canAccessPath("/feedingTable") ? [{ name: t("feeding Data"), path: "/feedingTable" }] : []),
              ...(canAccessPath("/feed") ? [{ name: t("add Feeding"), path: "/feed" }] : []),
              ...(canAccessPath("/feedlocationtable") ? [{ name: t("data By Location"), path: "/feedlocationtable" }] : []),
              ...(canAccessPath("/feedbylocation") ? [{ name: t("add  Location"), path: "/feedbylocation" }] : []),
            ].filter(Boolean),
          },
          {
            name: t("fodder"),
            icon: <FaBreadSlice />,
            subItems: [
              ...(canAccessPath("/fodderTable") ? [{ name: t("fodder Data"), path: "/fodderTable" }] : []),
              ...(canAccessPath("/fodder") ? [{ name: t("add Fodder"), path: "/fodder" }] : []),
            ].filter(Boolean),
          },
          ...(canAccessPath("/report") ? [{ name: t("reports"), icon: <FaChartBar />, path: "/report" }] : []),
        ].filter(item => {
          // Keep items with subItems if they have at least one subItem
          if (item.subItems) return item.subItems.length > 0;
          // Keep items without subItems (like reports)
          return true;
        }),
      },
      {
        title: t("System"),
        items: [
          {
            name: t("excluded"),
            icon: <FaExclamationTriangle />,
            subItems: [
              ...(canAccessPath("/excludedtable") ? [{ name: t("excluded Data"), path: "/excludedtable" }] : []),
              ...(canAccessPath("/excluded") ? [{ name: t("add Excluded"), path: "/excluded" }] : []),
            ].filter(Boolean),
          },
        ].filter(item => item.subItems && item.subItems.length > 0),
      },
      // RBAC Management Section
      ...(canViewRoles || canViewEmployees ? [{
        title: t("rbac.accessControl") || "Access Control",
        items: [
          ...(canViewRoles ? [{
            name: t("rbac.roles") || "Roles",
            icon: <FaUserShield />,
            subItems: [
              { name: t("rbac.allRoles") || "All Roles", path: "/roles" },
              { name: t("rbac.createRole") || "Create Role", path: "/roles/create" },
            ],
          }] : []),
          ...(canViewEmployees ? [{
            name: t("rbac.employees") || "Employees",
            icon: <FaUsers />,
            subItems: [
              { name: t("rbac.allEmployees") || "All Employees", path: "/employees" },
              { name: t("rbac.createEmployee") || "Create Employee", path: "/employees/create" },
            ],
          }] : []),
        ],
      }] : []),
    ];
    }

    // Filter out empty sections and items
    return menu
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          // Keep items with subItems if they have at least one subItem
          if (item.subItems) return item.subItems.length > 0;
          // Keep items without subItems
          return true;
        }),
      }))
      .filter(section => section.items.length > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, notificationCount, t, showUpgradeBadge, canViewRoles, canViewEmployees, checkPermission, permissions, isEmployee]);

  const toggleDropdown = (key) =>
    setActiveDropdown((cur) => (cur === key ? null : key));

  const dir = isRTL ? "rtl" : "ltr";
  const getTitle = (item) =>
    (typeof item === "string" ? item : item?.name || "");

  return (
    <aside
      className={`msb ${isOpen ? "open" : "closed"} ${dir} ${
        isOpen && hideRailWhenOpen ? "no-rail" : ""
      }`}
    >
      {/* RAIL */}
      {!(isOpen && hideRailWhenOpen) && (
        <div className="msb-rail">
          <div
            className="msb-logo"
            onClick={onToggle}
            aria-label="HALAL LAB"
            title="HALAL LAB"
            style={{ cursor: "pointer" }}
          >
            <div className="bars"><span/><span/><span/></div>
          </div>

          <div className="msb-rail-items">
            <Link to="/" className="rail-btn" title={t("Home")} data-tooltip={t("Home")} onClick={onToggle}>
              <IoHome />
            </Link>

            {!isAdmin && canAccessPath("/userDashboard") && (
              <Link to="/userDashboard" className="rail-btn" title={t("Dashboard")} data-tooltip={t("Dashboard")} onClick={onToggle}>
                <RiDashboardHorizontalLine />
              </Link>
            )}

            <button className="rail-btn" onClick={() => setLangOpen((v) => !v)} title={t("Language")} data-tooltip={t("Language")}>
              <MdOutlineLanguage />
            </button>

            {!isAdmin && (
              <Link to="/notificationPage" className="rail-btn badge-parent" title={t("Notifications")} data-tooltip={t("Notifications")} onClick={onToggle}>
                <FaBell />{notificationCount > 0 && <span className="badge">{notificationCount}</span>}
              </Link>
            )}

             <Link to="/support" className="rail-btn" title={t("Support")} data-tooltip={t("Support")} onClick={onToggle}>
              <FaHeadset />
            </Link>
          </div>

          <button className="rail-btn logout" onClick={onLogout} title={t("Logout")} data-tooltip={t("Logout")}>
            <CiLogout />
          </button>
        </div>
      )}

      {/* PANEL */}
      <div className="msb-panel">
        <div className="panel-header">
          <div className="panel-left">
            <button className="collapse-btn" onClick={onToggle} aria-label="Toggle sidebar">
              {isRTL ? <FaChevronRight /> : <FaChevronLeft />}
            </button>

            <div className="brand">
              <div className="mark" />
              <span>MAZRAA ONLINE</span>
            </div>
          </div>

          <div className="lang-switch">
            <button className={`chip ${langOpen ? "active" : ""}`} onClick={() => setLangOpen((v) => !v)}>
              <MdOutlineLanguage /><span>{t("Language")}</span>{langOpen ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {langOpen && (
              <div className="lang-menu">
                <button onClick={() => onChangeLanguage?.("en")}>{t("English")}</button>
                <button onClick={() => onChangeLanguage?.("ar")}>{t("العربية")}</button>
                <button onClick={() => onChangeLanguage?.("ur")}>{t("اردو")}</button>
              </div>
            )}
          </div>
        </div>

        <nav className="panel-nav">
          {baseMenu.map((section, sIdx) => {
            // Don't render section if it has no items
            if (!section.items || section.items.length === 0) {
              return null;
            }
            
            return (
              <div className="nav-section" key={sIdx}>
                <p className="section-title">{section.title}</p>
                <ul className="section-list">
                  {section.items.map((item, iIdx) => {
                  const hasChildren = !!item.subItems;
                  const active = pathname === item.path;
                  const k = `${section.title}-${item.name}`;

                  return (
                    <li className={`nav-item ${active ? "active" : ""}`} key={iIdx}>
                      {hasChildren ? (
                        <>
                          <button className="item-btn" onClick={() => toggleDropdown(k)} aria-expanded={activeDropdown === k} title={getTitle(item)}>
                            <span className="ico">{item.icon}</span>
                            <span className="txt">{item.name}</span>
                            <span className="chev">{activeDropdown === k ? <FaChevronUp /> : <FaChevronDown />}</span>
                          </button>

                          {activeDropdown === k && (
                            <ul className="submenu">
                              {item.subItems.map((sub, subIdx) => (
                                <li key={subIdx}>
                                  <Link
                                    to={sub.path}
                                    className={`sub-link ${pathname === sub.path ? "current" : ""}`}
                                    onClick={onToggle}
                                  >
                                    {sub.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : item.disabled ? (
                        <span
                          className="item-btn disabled"
                          title={getTitle(item)}
                          style={{ cursor: "not-allowed", opacity: 0.6 }}
                        >
                          <span className="ico">{item.icon}</span>
                          <span className="txt">{item.name}</span>
                          {item.badge && <span className="pill">{item.badge}</span>}
                        </span>
                      ) : (
                        <Link
                          to={item.path}
                          className="item-btn"
                          title={getTitle(item)}
                          onClick={onToggle}
                        >
                          <span className="ico">{item.icon}</span>
                          <span className="txt">{item.name}</span>
                          {item.badge && <span className="pill">{item.badge}</span>}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })}
        </nav>

        {/* Subscription Card */}
        {!isAdmin && !isEmployee() && <SidebarSubscriptionCard />}

        <button className="logout-wide" onClick={onLogout}>
          <CiLogout /><span>{t("Logout")}</span>
        </button>
      </div>
    </aside>
  );
}
